"use client";

import {
  createContext,
  useContext,
  useCallback,
  useSyncExternalStore,
  type ReactNode,
} from "react";

type Theme = "light" | "dark";

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = "praxis-ops-theme";
const THEME_EVENT = "praxis-ops-theme-change";

/**
 * The theme lives in localStorage (with the OS preference as the fallback),
 * i.e. outside React. It is read through useSyncExternalStore so the SSR
 * render and the first client render agree without a post-mount setState;
 * the anti-flash script has already applied the real theme to the DOM by then.
 */
function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "dark" || stored === "light") return stored;
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  } catch {
    return "light";
  }
}

function writeStoredTheme(next: Theme) {
  document.documentElement.setAttribute("data-theme", next);
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* storage unavailable (private mode); the theme still applies in-session */
  }
  // `storage` only fires in *other* tabs, so announce the change locally too.
  window.dispatchEvent(new Event(THEME_EVENT));
}

function subscribeTheme(onChange: () => void) {
  window.addEventListener(THEME_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(THEME_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // SSR-safe default — anti-flash script handles the DOM before paint.
  const theme = useSyncExternalStore<Theme>(
    subscribeTheme,
    readStoredTheme,
    () => "light",
  );

  const setTheme = useCallback((t: Theme) => writeStoredTheme(t), []);
  const toggleTheme = useCallback(
    () => writeStoredTheme(readStoredTheme() === "dark" ? "light" : "dark"),
    [],
  );

  return (
    <ThemeContext value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
